/**
 * History Indicator
 * Single button with dropdown menu for undo/redo actions
 */

import { useState, useRef, useEffect } from 'react';
import { History, Undo2, Redo2 } from 'lucide-react';
import { useHistoryStore, useCanUndo, useCanRedo, useUndoLabel, useRedoLabel } from '@/stores/historyStore';

interface HistoryIndicatorProps {
  className?: string;
}

export function HistoryIndicator({ className = '' }: HistoryIndicatorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const undo = useHistoryStore((s) => s.undo);
  const redo = useHistoryStore((s) => s.redo);
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();
  const undoLabel = useUndoLabel();
  const redoLabel = useRedoLabel();

  const hasHistory = canUndo || canRedo;

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleUndo = () => {
    undo();
    setIsOpen(false);
  };

  const handleRedo = () => {
    redo();
    setIsOpen(false);
  };

  return (
    <div ref={menuRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          p-2 rounded-lg transition-colors flex items-center gap-1.5
          ${isOpen
            ? 'bg-white/10 text-white'
            : hasHistory
              ? 'hover:bg-white/10 text-white/80 hover:text-white'
              : 'text-white/30'
          }
        `}
        title="History"
        aria-label="History"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <History size={16} />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-1 w-56 bg-navy-light border border-white/10 rounded-lg shadow-xl overflow-hidden z-50"
          role="menu"
        >
          <div className="p-1">
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              className={`
                w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm transition-colors
                ${canUndo
                  ? 'hover:bg-white/10 text-fg'
                  : 'text-fg-subtle cursor-not-allowed'
                }
              `}
              role="menuitem"
            >
              <Undo2 size={14} className="flex-none" />
              <div className="flex-1 min-w-0">
                <div className={canUndo ? 'text-fg' : 'text-fg-subtle'}>Undo</div>
                {canUndo && (
                  <div className="text-xs text-fg-muted truncate">{undoLabel}</div>
                )}
              </div>
              <kbd className="text-[10px] text-fg-subtle bg-white/5 px-1.5 py-0.5 rounded">
                {navigator.platform.includes('Mac') ? '⌘Z' : 'Ctrl+Z'}
              </kbd>
            </button>

            <button
              onClick={handleRedo}
              disabled={!canRedo}
              className={`
                w-full flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm transition-colors
                ${canRedo
                  ? 'hover:bg-white/10 text-fg'
                  : 'text-fg-subtle cursor-not-allowed'
                }
              `}
              role="menuitem"
            >
              <Redo2 size={14} className="flex-none" />
              <div className="flex-1 min-w-0">
                <div className={canRedo ? 'text-fg' : 'text-fg-subtle'}>Redo</div>
                {canRedo && (
                  <div className="text-xs text-fg-muted truncate">{redoLabel}</div>
                )}
              </div>
              <kbd className="text-[10px] text-fg-subtle bg-white/5 px-1.5 py-0.5 rounded">
                {navigator.platform.includes('Mac') ? '⇧⌘Z' : 'Ctrl+Y'}
              </kbd>
            </button>
          </div>

          {!hasHistory && (
            <div className="px-3 py-2 text-xs text-fg-subtle border-t border-white/5">
              No history yet
            </div>
          )}
        </div>
      )}
    </div>
  );
}
